import React, { useState, useRef } from "react";
import { postReply } from "../../../utils/utils";
import { theme } from "../../../theme/colors";
import { shape } from "../../../theme/shape";

const MAX_LEN = 1000;

const ReplyForm = ({ commentId, onReplyAdded }) => {
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);
  const [postAnonymously, setPostAnonymously] = useState(true);
  const remaining = MAX_LEN - reply.length;
  const formRef = useRef(null);

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!reply.trim() || loading) return;

    setLoading(true);
    try {
      const data = await postReply(commentId, reply.trim(), postAnonymously);
      onReplyAdded?.(data);
      setReply("");
    } catch (error) {
      console.error("Error posting reply:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") handleReplySubmit(e);
  };

  return (
    <form
      ref={formRef}
      onSubmit={handleReplySubmit}
      aria-busy={loading}
      className="mt-3 ml-4 w-full max-w-2xl space-y-3 md:ml-0"
    >
      <label htmlFor={`reply-${commentId}`} className="sr-only">
        Reply text
      </label>
      <textarea
        id={`reply-${commentId}`}
        value={reply}
        onChange={(e) => setReply(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Write a reply…"
        rows={3}
        disabled={loading}
        maxLength={MAX_LEN}
        aria-label="Reply text"
        className="w-full min-h-[72px] rounded-xl border border-gray-300 bg-white px-3 py-3 text-[0.95rem] leading-6 outline-none transition
                   placeholder:text-gray-400
                   focus:border-[#0e918c] focus:ring-4 focus:ring-[#0e918c]/15
                   disabled:bg-gray-100 disabled:text-gray-500"
        style={{ borderRadius: shape.radius.control }}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-gray-600 select-none">
          <input
            type="checkbox"
            checked={postAnonymously}
            onChange={() => setPostAnonymously((v) => !v)}
            className="h-4 w-4 accent-[#0e918c] cursor-pointer"
          />
          <span>Post as anonymous</span>
          {/* <span className="text-xs text-gray-500">Ctrl/⌘ + Enter to send</span> */}
        </label>

        <div className="flex items-center gap-3">
          <span
            className={`text-xs ${
              remaining < 50 ? "text-amber-700" : "text-gray-500"
            }`}
          >
            {remaining}
          </span>
          <button
            type="submit"
            disabled={loading || !reply.trim()}
            aria-disabled={loading || !reply.trim()}
            className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90
                       disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-white disabled:hover:opacity-100"
            style={{ backgroundColor: theme.primary, borderRadius: shape.radius.control }}
          >
            {loading ? "Replying…" : "Reply"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default ReplyForm;
